// ============================================================
// EscoreGlobal.js — o número que junta a técnica e a física
//
// Ele aparece em quatro lugares: na avaliação técnica, nos testes
// físicos, na ficha do atleta e no PDF. Por isso mora aqui, e não dentro
// de uma das telas: quatro cópias divergiriam na primeira vez que
// alguém mexesse em uma só.
//
// A conta não está aqui. Vem de `desempenho.js`, que é o mesmo lugar de
// onde o PDF e o Excel puxam.
//
// ── Por que a conta aparece junto do número ─────────────────
//
// "6,4" sozinho não diz nada a quem vai treinar o atleta. O quadro mostra
// as duas parcelas, com o peso de cada uma, porque a pergunta real nunca
// é o escore: é se o atleta está atrás na técnica ou no preparo, e as
// duas coisas levam a treinos diferentes.
// ============================================================

import { NIVEIS } from "../desempenho";
import {
  TEXTO, CINZA_ESC, BORDA, FUNDO_SUAVE, RAIO, ESP, TIPO, OK, ATENCAO, RUIM, AZUL,
} from "../tema";
import { Vazio } from "./Dados";

// A cor de cada faixa. Verde e vermelho dizem o que a palavra já diz, e
// sem eles a tabela da modalidade fica ilegível de longe.
const COR_DO_NIVEL = {
  muitoAbaixo: RUIM,
  abaixoDaMedia: RUIM,
  regular: ATENCAO,
  bom: OK,
  excelente: OK,
};

export function corDoNivel(nivel) {
  return (nivel && COR_DO_NIVEL[nivel.id]) || CINZA_ESC;
}

function pt(v, casas = 1) {
  return Number.isFinite(v) ? v.toFixed(casas).replace(".", ",") : "—";
}

export default function EscoreGlobal({ escore, compacto = false }) {
  if (!escore) return null;

  if (escore.global === null) {
    return (
      <div style={{ display: "grid", gap: ESP.sm }}>
        <Vazio titulo={
          `O escore global precisa das duas avaliações, e falta ${escore.faltam.join(" e ")}.`
        } />
        {/* A metade que existe continua legível. Ela é informação, e
            esconder o que foi feito porque a outra parte não foi
            punir quem avaliou. */}
        <Parcelas escore={escore} />
      </div>
    );
  }

  const cor = corDoNivel(escore.nivel);

  return (
    <div style={{ display: "grid", gap: ESP.md }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 12, flexWrap: "wrap" }}>
        <span style={{ fontSize: compacto ? 28 : 36, fontWeight: 800, color: cor, lineHeight: 1 }}>
          {pt(escore.global)}
        </span>
        <span style={{ ...TIPO.corpo, fontWeight: 700, color: cor }}>
          {escore.classificacao}
        </span>
        <span style={{ ...TIPO.apoio, color: CINZA_ESC }}>de 0 a 10</span>
      </div>

      {!compacto && escore.nivel && (
        <div style={{ ...TIPO.apoio, color: CINZA_ESC, lineHeight: 1.6 }}>
          {escore.nivel.descricao}
        </div>
      )}

      <Parcelas escore={escore} />

      {!compacto && (
        <div style={{ ...TIPO.apoio, color: CINZA_ESC, lineHeight: 1.6 }}>
          O escore é a média dos critérios técnicos com peso de 75%, mais a média das
          capacidades físicas com peso de 25%. É a mesma conta da planilha de performance
          do programa, e serve para acompanhar o atleta ao longo do ano. Avaliadores
          diferentes calibram a escala de modo diferente, então o número não compara
          atleta de uma modalidade com atleta de outra.
        </div>
      )}
    </div>
  );
}

function Parcelas({ escore }) {
  const linhas = [
    { rotulo: "Avaliação técnica", p: escore.tecnica },
    { rotulo: "Capacidades físicas", p: escore.fisica },
  ];
  return (
    <div style={{
      display: "grid", gap: 1, background: BORDA, border: `1px solid ${BORDA}`,
      borderRadius: RAIO.md, overflow: "hidden",
    }}>
      {linhas.map(({ rotulo, p }) => (
        <div key={rotulo} style={{
          display: "grid", gridTemplateColumns: "1fr auto auto auto",
          gap: 10, alignItems: "baseline", background: FUNDO_SUAVE, padding: "8px 11px",
        }}>
          <span style={{ ...TIPO.apoio, color: TEXTO, fontWeight: 600 }}>{rotulo}</span>
          <span style={{ ...TIPO.apoio, color: CINZA_ESC }}>
            {p.somatorio !== null && p.divisor
              ? `${pt(p.somatorio, 1)} em ${p.divisor} critérios`
              : "não avaliada"}
          </span>
          <span style={{ ...TIPO.apoio, color: TEXTO }}>
            média {pt(p.media, 2)}
          </span>
          <span style={{ ...TIPO.apoio, color: CINZA_ESC }}>
            {`× ${pt(p.peso * 100, 0)}% = ${pt(p.contribuicao, 2)}`}
          </span>
        </div>
      ))}
    </div>
  );
}

// ── A legenda das cinco faixas ───────────────────────────────
//
// Fica ao lado do escore em toda tela que o mostra, porque "Regular" é
// uma palavra que cada um lê de um jeito e aqui ela tem intervalo.
export function LegendaDosNiveis({ compacta = false }) {
  return (
    <div style={{ display: "grid", gap: 6, marginTop: ESP.md }}>
      <div style={{ ...TIPO.apoio, color: TEXTO, fontWeight: 600 }}>
        As cinco faixas do escore
      </div>
      {NIVEIS.map(n => (
        <div key={n.id} style={{ display: "flex", gap: 8, alignItems: "baseline" }}>
          <span style={{
            width: 8, height: 8, borderRadius: 4, background: corDoNivel(n),
            flexShrink: 0, marginTop: 5,
          }} />
          <span style={{ ...TIPO.apoio, color: TEXTO, fontWeight: 600, minWidth: 64 }}>
            {pt(n.de)} a {pt(n.ate)}
          </span>
          <span style={{ ...TIPO.apoio, color: TEXTO, minWidth: 130 }}>{n.rotulo}</span>
          {!compacta && (
            <span style={{ ...TIPO.apoio, color: CINZA_ESC, lineHeight: 1.6 }}>
              {n.descricao}
            </span>
          )}
        </div>
      ))}
    </div>
  );
}

// ── A escala de nota de 1 a 10 ───────────────────────────────
//
// A outra legenda, a das notas por critério, que é a escala em que o
// avaliador trabalha. Ela não é a mesma do escore: aqui são três faixas,
// e lá cinco.
// `escala` vem do ponto compilado e traz os nomes que a MODALIDADE dá a
// cada faixa, mais a observação dela. Hoje as três usam a escala comum
// do programa, que é a de baixo; o parâmetro existe para o dia em que
// uma delas precisar da régua própria. Sem ele, vale a comum.
const FAIXAS_PADRAO = [
  { id: "regular", de: 1, ate: 3, rotulo: "Regular" },
  { id: "intermediario", de: 4, ate: 7, rotulo: "Intermediário" },
  { id: "avancado", de: 8, ate: 10, rotulo: "Avançado" },
];

export function LegendaDaEscala({ escala = null }) {
  const faixas = (escala && escala.faixas) || FAIXAS_PADRAO;
  const intermediaria = faixas[1] || FAIXAS_PADRAO[1];
  return (
    <div style={{
      display: "flex", gap: 14, flexWrap: "wrap", alignItems: "baseline",
      background: FUNDO_SUAVE, border: `1px solid ${BORDA}`, borderRadius: RAIO.md,
      padding: "9px 12px", marginTop: ESP.md,
    }}>
      <span style={{ ...TIPO.apoio, color: TEXTO, fontWeight: 600 }}>Escala da nota</span>
      {faixas.map(f => (
        <span key={f.id} style={{ ...TIPO.apoio, color: CINZA_ESC }}>
          {`${f.de} a ${f.ate} ${f.rotulo.toLowerCase()}`}
        </span>
      ))}
      <span style={{ ...TIPO.apoio, color: AZUL }}>
        {`A nota aceita decimal, e 7,5 ainda é ${intermediaria.rotulo.toLowerCase()}.`}
      </span>
      {escala && escala.observacao && (
        <span style={{ ...TIPO.apoio, color: CINZA_ESC }}>{escala.observacao}</span>
      )}
    </div>
  );
}
